/**
 * SharedRenderer - Single WebGL context shared across the app
 *
 * Avoids creating multiple WebGL contexts (mobile browsers limit to ~8-16).
 * Features register a "view" with their own Scene + Camera, and the shared
 * renderer draws each active view every frame. When no views are registered
 * the render loop pauses automatically (zero GPU cost).
 *
 * Usage:
 *   const sr = SharedRenderer.instance;
 *   const viewId = sr.addView({ scene, camera, container });
 *   // ... later
 *   sr.removeView(viewId);
 */

import * as THREE from 'three';
import { renderScheduler } from './RenderScheduler';

export type RenderQuality = 'low' | 'medium' | 'high';

export interface SharedRendererConfig {
  quality?: RenderQuality;
  maxDpr?: number;
  /**
   * Hard ceiling on device pixel ratio that survives quality changes. Used to
   * cap the framebuffer (the single biggest GPU allocation) in the native app's
   * WKWebView, which has a much tighter GPU-memory budget than desktop browsers
   * — exceeding it loses the WebGL context (black render → crash). Default: none.
   */
  dprCeiling?: number;
}

export interface ViewOptions {
  scene: THREE.Scene;
  camera: THREE.Camera;
  /** DOM element the view renders into. Canvas is sized to match. */
  container: HTMLElement;
  /** Priority for render order (lower = first). Default 0. */
  priority?: number;
  /** Called every frame before this view is rendered. */
  onBeforeRender?: () => void;
  /**
   * Externally driven view. When true, this view is NOT drawn by the
   * SharedRenderer's own rAF loop — its owner renders it (e.g. the in-game
   * Engine game loop, which must render AFTER physics interpolation in the
   * same frame). Leaving this false for such a view double-renders the scene
   * every frame (Engine loop + this loop).
   */
  manual?: boolean;
  /**
   * Frame cap for this auto view. Defaults to the ambience cap (30fps —
   * right for slow sways like the login dice). Pass Infinity to render on
   * every rAF tick (the bonus suitcase: falling cash physics and a punchy
   * reveal read choppy below native rate).
   */
  maxFps?: number;
}

interface ViewEntry extends Required<Pick<ViewOptions, 'priority'>> {
  id: string;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  container: HTMLElement;
  onBeforeRender?: () => void;
  manual: boolean;
  maxFps: number;
}

export const QUALITY_SETTINGS: Record<RenderQuality, {
  shadowMapSize: number;
  maxDpr: number;
  antialias: boolean;
  walletModel: 'low' | 'medium' | 'high';
  billSegments: number;
  billShadows: boolean;
  billTextureSize: number;
}> = {
  low: {
    shadowMapSize: 512,
    maxDpr: 1,
    antialias: false,
    walletModel: 'low',
    billSegments: 4,
    billShadows: false,
    billTextureSize: 256,
  },
  medium: {
    shadowMapSize: 1024,
    maxDpr: 1.5,
    antialias: true,
    walletModel: 'medium',
    billSegments: 6,
    billShadows: true,
    billTextureSize: 512,
  },
  high: {
    // 1024 — 512 was too boxy for SDL's close shooting camera (a ~1-unit die
    // shadow spanned only ~17 texels over the 30-unit shadow frustum). 1024
    // doubles texel density (~34 texels/die) for smooth edges at ~4 MB depth
    // VRAM — affordable now that the PMREM env-map hog (the real context-loss
    // cause) is gone. The old 2048 PCFSoft was 4× this and overkill. Re-applied
    // to the served scene's lights after load (see main.ts engine.setQuality).
    shadowMapSize: 1024,
    maxDpr: 2,
    antialias: true,
    walletModel: 'high',
    billSegments: 8,  // matches the pre-tier hardcoded value — no regression for the default 'high' tier
    billShadows: true,
    billTextureSize: 1024,
  },
};

let nextViewId = 0;

export class SharedRenderer {
  private static _instance: SharedRenderer | null = null;

  readonly gl: THREE.WebGLRenderer;

  private quality: RenderQuality;
  private maxDpr: number;
  private dprCeiling: number;
  /** Runtime dynamic-resolution cap, driven by AdaptiveResolution. Infinity = no
   *  effect (the default). Lowered when the device can't hold frame-rate, raised
   *  back via gated probes. Framebuffer cost is ∝ DPR², so this is the main lever. */
  private adaptiveDprCap = Infinity;
  /** True between webglcontextlost and webglcontextrestored — render is paused. */
  private contextLost = false;
  /** Frame cap for ambience auto views (login dice / bonus suitcase). */
  private static readonly AUTO_VIEW_FPS = 30;
  private lastAutoRenderTime = 0;
  private views = new Map<string, ViewEntry>();
  private sortedViews: ViewEntry[] = [];
  private animationId: number | null = null;
  private isVisible = true;
  private boundTick = this.tick.bind(this);

  /** Get or create the singleton. Call init() once at app start. */
  static get instance(): SharedRenderer {
    if (!SharedRenderer._instance) {
      throw new Error('SharedRenderer not initialized. Call SharedRenderer.init() first.');
    }
    return SharedRenderer._instance;
  }

  static init(config: SharedRendererConfig = {}): SharedRenderer {
    if (SharedRenderer._instance) return SharedRenderer._instance;
    SharedRenderer._instance = new SharedRenderer(config);
    return SharedRenderer._instance;
  }

  private constructor(config: SharedRendererConfig) {
    this.quality = config.quality ?? 'high';
    const settings = QUALITY_SETTINGS[this.quality];
    this.maxDpr = config.maxDpr ?? settings.maxDpr;
    this.dprCeiling = config.dprCeiling ?? Infinity;

    // Create the one and only WebGL renderer
    // Canvas starts detached; it's moved into view containers as needed
    this.gl = new THREE.WebGLRenderer({
      antialias: settings.antialias,
      powerPreference: 'high-performance',
      stencil: false,
      depth: true,
      alpha: true,
    });

    this.gl.setPixelRatio(this.dpr());
    this.gl.outputColorSpace = THREE.SRGBColorSpace;
    this.gl.toneMapping = THREE.ACESFilmicToneMapping;
    this.gl.toneMappingExposure = 1.0;
    this.gl.shadowMap.enabled = true;
    // VSM (Variance Shadow Map): stores depth+depth² and Gaussian-blurs the
    // shadow map (shadow.blurSamples), so the penumbra reads SMOOTH at modest
    // resolution — PCFSoft samples raw depth and stair-steps the edge. This is
    // Phone Dice's approach (dice-engine-driver.js). VSM is finicky about depth
    // precision (a wide shadow-camera near/far bands it) and can light-bleed, so
    // every shadow caster MUST set a tight near/far + bias/normalBias: the served
    // scene's spot in Renderer.setQuality, LoginDiceScene's spot inline. Global
    // type → softens the dice-preview scenes too (BonusSuitcase casts no shadow).
    this.gl.shadowMap.type = THREE.VSMShadowMap;
    // On-demand shadow pass. The VSM depth render + 8-sample Gaussian blur is
    // one of the most expensive parts of a frame, and casters (dice, bills,
    // wallet) are static on most drawn frames — camera lerps, input over UI,
    // label repaints. Movers call renderScheduler.markShadowsDirty(); the
    // in-game render driver (Renderer.render) flips needsUpdate through
    // renderScheduler.shouldRenderShadows(), which also re-renders on a 150ms
    // staleness ceiling so a missed mover degrades to a slightly-late shadow,
    // never a frozen one. Auto views (login/preview scenes, tick() below)
    // refresh shadows every frame — unchanged from autoUpdate behavior.
    this.gl.shadowMap.autoUpdate = false;
    this.gl.shadowMap.needsUpdate = true;
    this.gl.autoClear = false;

    // Pause rendering when tab is hidden
    document.addEventListener('visibilitychange', () => {
      this.isVisible = !document.hidden;
      // Returning to the foreground: pulse so the in-game view redraws at full
      // rate immediately instead of waiting for the idle heartbeat, and rebuild
      // shadows — the world may have changed while backgrounded.
      if (this.isVisible) {
        renderScheduler.pulse(500);
        renderScheduler.markShadowsDirty();
      }
    });

    // WebGL context-loss RESILIENCE + RECOVERY. WKWebView drops the GL context
    // under memory pressure / backgrounding / Jetsam no matter how lean we are —
    // so production must RECOVER, not just survive. preventDefault() asks the
    // browser to restore; we pause our render loops immediately so nothing draws
    // or disposes into the dead context. If WKWebView actually fires
    // 'restored' (it often does NOT — the canvas just stays black), we resume.
    // If it doesn't restore within a grace window, we RELOAD: the app re-inits
    // WebGL fresh from the CDN and the player drops back into the live game via
    // the resume-safe state:full path. Loop-guarded (sessionStorage) so a
    // persistent OOM can't reload-storm — after 3 losses in a minute we stop
    // auto-reloading and leave it paused rather than thrash.
    const canvas = this.gl.domElement;
    let lossReloadTimer: number | null = null;
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.contextLost = true;
      console.warn('[SharedRenderer] WebGL context LOST');
      if (lossReloadTimer !== null) return;
      lossReloadTimer = window.setTimeout(() => {
        lossReloadTimer = null;
        if (!this.contextLost) return; // restored during the grace window
        const now = Date.now();
        let log: number[] = [];
        try { log = JSON.parse(sessionStorage.getItem('sdl_ctxloss') || '[]'); } catch { /* ignore */ }
        log = log.filter((t) => now - t < 60_000);
        if (log.length >= 3) {
          console.warn('[SharedRenderer] context lost 3x/min — not auto-reloading');
          return;
        }
        log.push(now);
        try { sessionStorage.setItem('sdl_ctxloss', JSON.stringify(log)); } catch { /* ignore */ }
        console.warn('[SharedRenderer] context not restored — reloading to recover');
        location.reload();
      }, 2000);
    }, false);
    canvas.addEventListener('webglcontextrestored', () => {
      this.contextLost = false;
      if (lossReloadTimer !== null) { clearTimeout(lossReloadTimer); lossReloadTimer = null; }
      console.warn('[SharedRenderer] WebGL context RESTORED — render resumed');
    }, false);
  }

  /** Resolved device pixel ratio: tier maxDpr ∩ hard ceiling ∩ adaptive cap. */
  private dpr(): number {
    return Math.min(window.devicePixelRatio, this.maxDpr, this.dprCeiling, this.adaptiveDprCap);
  }

  /** Highest DPR this device/tier can reach BEFORE the adaptive cap — the ceiling
   *  AdaptiveResolution scales down from. */
  get maxResolvableDpr(): number {
    return Math.min(window.devicePixelRatio, this.maxDpr, this.dprCeiling);
  }

  /**
   * Set the dynamic-resolution cap and re-rasterize the framebuffer immediately.
   * No-op if unchanged. A DPR-only change doesn't alter CSS size, so resizeIfNeeded
   * (which keys on w/h) wouldn't catch it — we re-apply setPixelRatio + setSize on
   * the canvas's current container directly, and invalidate the auto-loop's size
   * cache so its next tick re-applies too.
   */
  setAdaptiveDprCap(cap: number): void {
    if (cap === this.adaptiveDprCap) return;
    this.adaptiveDprCap = cap;
    this.gl.setPixelRatio(this.dpr());
    const parent = this.gl.domElement.parentElement;
    if (parent && parent.clientWidth && parent.clientHeight) {
      this.gl.setSize(parent.clientWidth, parent.clientHeight, false);
    }
    this.lastW = 0;
    this.lastH = 0;
    renderScheduler.pulse(300);
  }

  /** True while the WebGL context is lost. Owners of manual views must skip rendering. */
  get isContextLost(): boolean {
    return this.contextLost;
  }

  /** Resolved device pixel ratio (tier maxDpr ∩ hard ceiling). Manual-view owners
   *  must use this when sizing so the native DPR cap applies in-game too. */
  get pixelRatio(): number {
    return this.dpr();
  }

  /**
   * Register a view to be rendered each frame.
   * The shared canvas is appended to `container` and sized to fit.
   */
  addView(opts: ViewOptions): string {
    const id = `view_${nextViewId++}`;
    const entry: ViewEntry = {
      id,
      scene: opts.scene,
      camera: opts.camera as THREE.PerspectiveCamera,
      container: opts.container,
      priority: opts.priority ?? 0,
      onBeforeRender: opts.onBeforeRender,
      manual: opts.manual ?? false,
      maxFps: opts.maxFps ?? SharedRenderer.AUTO_VIEW_FPS,
    };

    this.views.set(id, entry);
    this.rebuildSortedViews();

    // Always attach canvas to the highest-priority view's container
    const activeView = this.sortedViews[this.sortedViews.length - 1];
    this.attachCanvasTo(activeView.container);
    this.resizeToView(activeView);

    // Start loop only when there's a view for it to draw. Manual views are
    // rendered by their owner's loop (the Engine), so a manual-only registry
    // would just burn an idle rAF every frame.
    if (this.hasAutoView()) this.startLoop();

    return id;
  }

  /** Remove a view. Loop pauses when no auto views remain. */
  removeView(id: string): void {
    this.views.delete(id);
    this.rebuildSortedViews();

    if (!this.hasAutoView()) this.stopLoop();

    if (this.views.size === 0) {
      // Detach canvas
      this.gl.domElement.remove();
    } else {
      this.attachCanvasTo(this.sortedViews[0].container);
    }
  }

  /** Check if a view exists. */
  hasView(id: string): boolean {
    return this.views.has(id);
  }

  /** Get the canvas element. */
  get canvas(): HTMLCanvasElement {
    return this.gl.domElement;
  }

  /** Current quality level. */
  getQuality(): RenderQuality {
    return this.quality;
  }

  /** Get betting-specific quality settings. */
  getBettingQualitySettings() {
    const s = QUALITY_SETTINGS[this.quality];
    return {
      walletModel: s.walletModel,
      billSegments: s.billSegments,
      billShadows: s.billShadows,
      billTextureSize: s.billTextureSize,
    };
  }

  /** Change render quality. */
  setQuality(quality: RenderQuality): void {
    this.quality = quality;
    const settings = QUALITY_SETTINGS[quality];
    this.maxDpr = settings.maxDpr;
    this.gl.setPixelRatio(this.dpr());
  }

  /** Force a resize for a specific view. */
  resizeToView(entry: ViewEntry): void {
    const w = entry.container.clientWidth;
    const h = entry.container.clientHeight;
    if (w === 0 || h === 0) return;

    const dpr = this.dpr();
    this.gl.setPixelRatio(dpr);
    this.gl.setSize(w, h, false);

    // Update canvas CSS size
    this.gl.domElement.style.width = `${w}px`;
    this.gl.domElement.style.height = `${h}px`;

    if ((entry.camera as THREE.PerspectiveCamera).isPerspectiveCamera) {
      const cam = entry.camera as THREE.PerspectiveCamera;
      cam.aspect = w / h;
      cam.updateProjectionMatrix();
    }
  }

  // ── Private ──────────────────────────────────────────

  private attachCanvasTo(container: HTMLElement): void {
    const canvas = this.gl.domElement;
    if (canvas.parentElement !== container) {
      canvas.style.display = 'block';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      container.appendChild(canvas);
    }
  }

  private rebuildSortedViews(): void {
    this.sortedViews = [...this.views.values()].sort((a, b) => a.priority - b.priority);
  }

  private startLoop(): void {
    if (this.animationId !== null) return;
    this.animationId = requestAnimationFrame(this.boundTick);
  }

  private stopLoop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  private tick(): void {
    // Render the highest-priority view that is NOT externally driven.
    // "manual" views (the in-game Renderer) are drawn by their owner's own
    // loop — the Engine game loop, after physics interpolation — so drawing
    // them here too would render the scene twice per frame.
    const v = this.activeAutoView();
    if (!v) {
      // No auto view left to draw — park the loop. addView() re-arms it
      // the next time an auto view registers.
      this.animationId = null;
      return;
    }

    // Per-view frame cap. Ambience scenes (login dice sway) default to 30fps
    // — 60fps + a shadow re-render per frame is pure battery burn there. The
    // suitcase opts into Infinity (every rAF tick) for its cash physics. The
    // `%` keeps cadence stable against rAF jitter instead of beating to 20.
    const interval = 1000 / v.maxFps;
    if (Number.isFinite(interval) && interval > 0) {
      const now = performance.now();
      const elapsed = now - this.lastAutoRenderTime;
      if (elapsed < interval - 1) {
        this.animationId = requestAnimationFrame(this.boundTick);
        return;
      }
      this.lastAutoRenderTime = now - (elapsed % interval);
    }

    if (this.isVisible && !this.contextLost) {
      // Move canvas to the active view's container if needed
      this.attachCanvasTo(v.container);
      this.resizeIfNeeded(v);
      v.onBeforeRender?.();
      // Auto views animate continuously and have no dirty-marking — refresh
      // shadows on every RENDERED frame (now capped, not per-rAF).
      this.gl.shadowMap.needsUpdate = true;
      this.gl.clear();
      this.gl.render(v.scene, v.camera);
    }

    this.animationId = requestAnimationFrame(this.boundTick);
  }

  /** True when at least one registered view is drawn by this loop. */
  private hasAutoView(): boolean {
    return this.sortedViews.some((v) => !v.manual);
  }

  /**
   * Highest-priority view this loop is responsible for rendering.
   * Excludes `manual` views, which are driven by their owner's render loop.
   */
  private activeAutoView(): ViewEntry | null {
    for (let i = this.sortedViews.length - 1; i >= 0; i--) {
      if (!this.sortedViews[i].manual) return this.sortedViews[i];
    }
    return null;
  }

  private lastW = 0;
  private lastH = 0;

  private resizeIfNeeded(entry: ViewEntry): void {
    const w = entry.container.clientWidth;
    const h = entry.container.clientHeight;
    if (w === this.lastW && h === this.lastH) return;
    this.lastW = w;
    this.lastH = h;
    this.resizeToView(entry);
  }

  /** Dispose everything. Only call on app shutdown. */
  dispose(): void {
    this.stopLoop();
    this.views.clear();
    this.sortedViews = [];
    this.gl.dispose();
    this.gl.domElement.remove();
    SharedRenderer._instance = null;
  }
}
